import { useEffect, useCallback } from "react";

import { Box, Button, Typography } from "@mui/material";
import SpotReviewImagePreviewItem from "./SpotReviewImagePreviewItem";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";

import { styled } from "@mui/material/styles";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import {
    SortableContext,
    rectSortingStrategy,
    arrayMove,
} from "@dnd-kit/sortable";

const VisuallyHiddenInput = styled("input")({
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: 1,
    overflow: "hidden",
    position: "absolute",
    bottom: 0,
    left: 0,
    whiteSpace: "nowrap",
    width: 1,
});

const UploadPhotoCreationStep = (
    {
        spotReview,
        setSpotReview,
        files,
        setFiles,
        previewUrls,
        setPreviewUrls,
        backButton,
        handleNext,
        isEdit,
        existingPhotoIds,
        setExistingPhotoIds,
        onDeletePhoto,
    }
) => {
    useEffect(() => {
        if (!files || files.length === 0) {
            return;
        }
        // In edit mode, files[i] is null for existing server photos (their
        // preview URLs are already set). Only regenerate blob URLs for new
        // File objects; keep existing URLs intact.
        setPreviewUrls((prev) =>
            files.map((file, i) => {
                if (file === null) return prev[i] || '';
                return URL.createObjectURL(file);
            })
        );
    }, [files, setPreviewUrls]);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIndex = parseInt(active.id);
        const newIndex = parseInt(over.id);
        setFiles((prev) => arrayMove(prev, oldIndex, newIndex));
        setPreviewUrls((prev) => arrayMove(prev, oldIndex, newIndex));
        if (setExistingPhotoIds) {
            setExistingPhotoIds((prev) => arrayMove(prev, oldIndex, newIndex));
        }
    }, [setFiles, setPreviewUrls, setExistingPhotoIds]);

    const onChange = (event) => {
        const newFiles = Array.from(event.target.files);
        setFiles(files.concat(newFiles));
        setSpotReview({
            ...spotReview,
            photos: newFiles,
        });
        if (setExistingPhotoIds) {
            setExistingPhotoIds((prev) => [...prev, ...newFiles.map(() => null)]);
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 1, width: '100%', flex: 1 }}>
            <Box>
                <Box component="h3" sx={{ m: 0, fontSize: '1.25rem', fontWeight: 600, color: 'text.primary' }}>
                    Upload your photos
                </Box>
                <Box sx={{ fontSize: '0.85rem', color: 'text.secondary', mt: 0.5 }}>
                    Share your best shots from this spot.
                </Box>
            </Box>

            {previewUrls.length === 0 ? (
                <Box
                    component="label"
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.5,
                        py: 5,
                        border: '2px dashed',
                        borderColor: 'primary.light',
                        borderRadius: 2,
                        bgcolor: 'grey.50',
                        cursor: 'pointer',
                        transition: 'all 200ms ease',
                        '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'primary.50',
                        },
                    }}
                >
                    <CloudUploadIcon sx={{ fontSize: 48, color: 'primary.light' }} />
                    <Typography variant="body1" sx={{ fontWeight: 500, color: 'text.secondary' }}>
                        Drag & drop or click to upload
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                        JPG, PNG, HEIC — up to 9 photos
                    </Typography>
                    <VisuallyHiddenInput
                        name="photos"
                        type="file"
                        accept="image/*"
                        maxLength={9 - previewUrls.length}
                        onChange={onChange}
                        multiple
                    />
                </Box>
            ) : (
                <>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={previewUrls.map((_, i) => String(i))} strategy={rectSortingStrategy}>
                            <Box
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)' },
                                    gap: 1,
                                    width: '100%',
                                }}
                            >
                                {previewUrls.map((previewUrl, idx) => (
                                    <SpotReviewImagePreviewItem src={previewUrl} id={String(idx)} key={previewUrl + '-' + idx} onDelete={() => {
                                        if (onDeletePhoto) {
                                            // Edit mode: delegate to parent (handles server DELETE for existing photos).
                                            onDeletePhoto(idx);
                                        } else {
                                            let newFiles = [];
                                            let newPreviewUrls = [];
                                            for (let i = 0; i < previewUrls.length; i++) {
                                                if (i !== idx) {
                                                    newFiles.push(files[i]);
                                                    newPreviewUrls.push(previewUrls[i]);
                                                }
                                            }
                                            setFiles(newFiles);
                                            setPreviewUrls(newPreviewUrls);
                                        }
                                    }} />
                                ))}
                            </Box>
                        </SortableContext>
                    </DndContext>
                    <Button
                        component="label"
                        variant="text"
                        startIcon={<AddPhotoAlternateIcon />}
                        sx={{
                            alignSelf: 'flex-start',
                            textTransform: 'none',
                            fontWeight: 500,
                            color: 'primary.main',
                        }}
                    >
                        Add more photos
                        <VisuallyHiddenInput
                            name="photos"
                            type="file"
                            accept="image/*"
                            maxLength={9 - previewUrls.length}
                            onChange={onChange}
                            multiple
                        />
                    </Button>
                </>
            )}

            <Box sx={{ flexGrow: 1 }} />
            <Box sx={{ display: 'flex', gap: 1.5 }}>
                {backButton}
                <Button
                    onClick={handleNext}
                    variant="contained"
                    color="primary"
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3 }}
                >
                    {previewUrls.length === 0 ? 'Skip' : 'Next'}
                </Button>
            </Box>
        </Box>
    );
}

export default UploadPhotoCreationStep;
